// The far ring's LOOK keyed by OUTER CHUNK (R5d flip precondition 3,
// APP_HORIZON.md §8c "look by chunk"). PURE (no three, no fetch): ring.js
// buildRingTex builds it (rt.look), scripts/check-ring.mjs pins it.
//
// The look = meta.panorama.classes alone (format ring-classes-2: per sector
// tier, px and the game-space rect incl. its gutter of the cls / lit / res
// rasters, which ringgen paints into the sector's texture). It names no ring
// tile: the ring tiles' meshes (panorama.tiles) retire with the far flip, the
// outer GROUND-M nodes that replace them ask the look per chunk.
//
//   chunk      the 2048 m square [cx m, (cx + 1) m) x [cz m, (cz + 1) m) in
//              game metres (LOOK_CHUNK_M = a GROUND-M L4 node side = the outer
//              engine chunk; cx / cz may be negative).
//   use rect   a sector's classes rect less one texel per side (the hold of a
//              bilinear sample: ring.js ringRectClamp).
//   chunk list every sector whose use rect meets the chunk, by tier then id;
//              K = the longest list.
//   own        a sector's own square, estimated: the classes rect less
//              (gutter_px[tier] + 0.5) cells per side (the raster window is
//              the own square out to whole cells + the gutter, so the estimate
//              is within half a cell of the true edge).
//   at(x, z)   the sector of the chunk list whose use rect holds (x, z) and
//              whose OWN square holds it deepest (the largest signed margin to
//              the own sides, in m; ties: list order) -> { id, u, v } with
//              stripMaterial's uv (v 1 = the rect's north edge), or null (the
//              frame's inside past the gutters, beyond the ring). That is the
//              sector whose own square holds the point (the ring tile ring.js
//              draws there today) except within half a cell of an own edge,
//              where both rasters hold the same class data; check-ring's look
//              row measures it. A margin in the use rect alone is no rule: a
//              coarser tier's gutter is wider in m and would win inside the
//              finer tier's own square.
//   table(K)   the chunk lists packed for a device lookup texture: Int16 sector
//              indices (into look.sectors), -1 padded, rows north to south
//              (cz0 first), K per chunk; a chunk with more than K sectors
//              throws (never a dropped candidate).
export const LOOK_CHUNK_M = 2048;

export function lookUseRect(q) {
  const [x0, z0, x1, z1] = q.rect;
  const tx = (x1 - x0) / q.px[0], tz = (z1 - z0) / q.px[1];
  return [x0 + tx, z0 + tz, x1 - tx, z1 - tz];
}

const margin = (r, x, z) => Math.min(x - r[0], r[2] - x, z - r[1], r[3] - z);

export function lookOwnRect(classes, q) {
  const [x0, z0, x1, z1] = q.rect, g = classes.gutter_px[q.tier] + 0.5;
  const tx = g * (x1 - x0) / q.px[0], tz = g * (z1 - z0) / q.px[1];
  return [x0 + tx, z0 + tz, x1 - tx, z1 - tz];
}

export function ringLook(classes, chunkM = LOOK_CHUNK_M) {
  const sectors = Object.entries(classes.sectors)
    .map(([id, q]) => ({ id, tier: q.tier, rect: q.rect, use: lookUseRect(q), own: lookOwnRect(classes, q) }))
    .sort((a, b) => a.tier - b.tier || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
  const cells = new Map();
  let cx0 = Infinity, cz0 = Infinity, cx1 = -Infinity, cz1 = -Infinity, K = 0;
  sectors.forEach((s, i) => {
    const u = s.use;
    for (let cz = Math.floor(u[1] / chunkM); cz * chunkM < u[3]; cz++) {
      for (let cx = Math.floor(u[0] / chunkM); cx * chunkM < u[2]; cx++) {
        const k = `${cx},${cz}`;
        const l = cells.get(k) || [];
        l.push(i);
        cells.set(k, l);
        K = Math.max(K, l.length);
        cx0 = Math.min(cx0, cx); cz0 = Math.min(cz0, cz); cx1 = Math.max(cx1, cx); cz1 = Math.max(cz1, cz);
      }
    }
  });
  const list = (cx, cz) => cells.get(`${cx},${cz}`) || [];
  return {
    chunkM, sectors, K,
    box: sectors.length ? [cx0, cz0, cx1 + 1, cz1 + 1] : [0, 0, 0, 0],
    chunk: (cx, cz) => list(cx, cz).map((i) => sectors[i].id),
    at(x, z) {
      let best = null, bm = -Infinity;
      for (const i of list(Math.floor(x / chunkM), Math.floor(z / chunkM))) {
        if (margin(sectors[i].use, x, z) < 0) continue;
        const m = margin(sectors[i].own, x, z);
        if (m > bm) { bm = m; best = sectors[i]; }
      }
      if (!best) return null;
      const r = best.rect;
      return { id: best.id, u: (x - r[0]) / (r[2] - r[0]), v: 1 - (z - r[1]) / (r[3] - r[1]) };
    },
    table(k = K) {
      const [bx0, bz0, bx1, bz1] = this.box, nx = bx1 - bx0, nz = bz1 - bz0;
      const data = new Int16Array(nx * nz * k).fill(-1);
      for (let cz = bz0; cz < bz1; cz++) {
        for (let cx = bx0; cx < bx1; cx++) {
          const l = list(cx, cz);
          if (l.length > k) throw new Error(`ring look: chunk ${cx},${cz} meets ${l.length} sectors > K ${k}`);
          data.set(l, ((cz - bz0) * nx + (cx - bx0)) * k);
        }
      }
      return { cx0: bx0, cz0: bz0, nx, nz, K: k, data };
    },
  };
}
